import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronDown,
  ChevronUp,
  Zap,
  FileText,
  TrendingUp
} from 'lucide-react';
import useAuthStore from '../store/authStore';

/**
 * Quick Stats Panel for sidebar
 * Shows collapsible sections with user stats
 */
const QuickStatsPanel = ({
  connects,
  connectsStatus,
  connectsMessage,
  upworkConnected = false,
  proposalsSent,
  successRate,
  className = ''
}) => {
  const [expandedSections, setExpandedSections] = useState({
    connects: true,
    proposals: true
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const { user } = useAuthStore();
  const canManageIntegrations = ['admin', 'superuser', 'owner'].includes(user?.role);
  const integrationsPath = canManageIntegrations ? '/admin/integrations' : '/profile';

  // Check if we have real data
  const hasConnectsData = typeof connects === 'number' && Number.isFinite(connects);
  const hasProposalData = proposalsSent !== undefined && proposalsSent !== null;

  return (
    <div className={`bg-white rounded-lg border border-gray-200 divide-y divide-gray-100 ${className}`}>
      {/* Profile Card */}
      {user && (
        <div className="px-4 py-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-semibold text-primary-700">
              {user.name?.charAt(0)?.toUpperCase() || '?'}
            </span>
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{user.name}</p>
            <p className="text-xs text-gray-500 capitalize">{user.role}</p>
          </div>
        </div>
      )}

      {/* Connects Section */}
      <CollapsibleStatSection
        title="Connects"
        icon={<Zap className="w-4 h-4 text-accent-500" />}
        isExpanded={expandedSections.connects}
        onToggle={() => toggleSection('connects')}
      >
        {hasConnectsData ? (
          <>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-gray-900">{connects}</span>
              <span className="text-sm text-gray-500">available</span>
            </div>
            <Link
              to={integrationsPath}
              className="text-xs text-secondary-600 hover:text-secondary-700 mt-2 inline-block"
            >
              {canManageIntegrations ? 'Manage Upwork connection' : 'View profile'}
            </Link>
          </>
        ) : upworkConnected ? (
          <>
            <p className="text-sm text-gray-500">
              {connectsMessage || 'Connects balance is not available from the current Upwork API response.'}
            </p>
            {connectsStatus && (
              <p className="text-xs text-gray-400 mt-1">
                Status: {connectsStatus.replace(/_/g, ' ')}
              </p>
            )}
          </>
        ) : (
          <>
            <p className="text-sm text-gray-500">
              Connect your Upwork account to see your profile and connects data.
            </p>
            <Link
              to={integrationsPath}
              className="text-xs text-secondary-600 hover:text-secondary-700 mt-2 inline-block"
            >
              {canManageIntegrations ? 'Connect Upwork' : 'View profile'}
            </Link>
          </>
        )}
      </CollapsibleStatSection>

      {/* Proposals Section */}
      <CollapsibleStatSection
        title="Proposals"
        icon={<FileText className="w-4 h-4 text-primary-500" />}
        isExpanded={expandedSections.proposals}
        onToggle={() => toggleSection('proposals')}
      >
        {hasProposalData ? (
          <>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Active proposals</span>
                <span className="text-sm font-medium text-gray-900">{proposalsSent}</span>
              </div>
              {successRate !== undefined && successRate !== null && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Success rate</span>
                  <span className="text-sm font-medium text-gray-900 flex items-center gap-1">
                    <TrendingUp className="w-3 h-3 text-success-500" />
                    {successRate}%
                  </span>
                </div>
              )}
            </div>
            <Link
              to="/proposals"
              className="text-xs text-secondary-600 hover:text-secondary-700 mt-2 inline-block"
            >
              View all proposals
            </Link>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-500 mb-2">
              Track your proposal activity here.
            </p>
            <Link
              to="/proposals"
              className="text-xs text-secondary-600 hover:text-secondary-700 inline-block"
            >
              View all proposals
            </Link>
          </>
        )}
      </CollapsibleStatSection>

    </div>
  );
};

/**
 * Collapsible Stat Section Component
 */
const CollapsibleStatSection = ({
  title,
  icon,
  isExpanded,
  onToggle,
  children
}) => {
  return (
    <div className="px-4 py-3">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-sm font-medium text-gray-900">{title}</span>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        )}
      </button>
      {isExpanded && (
        <div className="mt-3 pl-6">
          {children}
        </div>
      )}
    </div>
  );
};

export default QuickStatsPanel;
